import { css } from '@emotion/react';

import { su, theme, typography } from '@adflow/frontend/ui-theme';

import type { InputStatus } from '../Inputs';
import { inputStyles } from '../Inputs';

const baseContainer = ({ error, disabled, archived, readOnly }: InputStatus) =>
	css(
		inputStyles.inputContainer,
		error && inputStyles.inputContainerError,
		disabled && inputStyles.inputContainerDisabled,
		archived && inputStyles.inputContainerArchived,
		readOnly && inputStyles.inputContainerReadOnly,
		// Remove reach's :focus styles
		{
			'&:focus': css({
				outline: '2px solid transparent',
				outlineOffset: '2px',
			}),
		}
	);

const baseInput = ({ disabled, archived, readOnly }: InputStatus) =>
	css(
		inputStyles.input,
		disabled && inputStyles.inputContainerDisabled,
		archived && inputStyles.inputArchived,
		readOnly && inputStyles.inputReadOnly
	);

export const selectorStyles = {
	container: (status: InputStatus) => baseContainer(status),
	multiselectContainer: (status: InputStatus) =>
		css(
			baseContainer(status),
			/**
			 * Because multiselect layout accommodates in-container item display,
			 * container has different styles
			 */
			su.roundedSm,
			{
				flexWrap: 'wrap',
				alignItems: 'center',
				justifyContent: 'flex-start',
				gap: '0.25rem',
				paddingLeft: '0.25rem',
				paddingRight: '0.25rem',
				paddingTop: '0.125rem',
				paddingBottom: '0.125rem',
				minHeight: 42,
			},
			status.disabled || status.readOnly ? { cursor: 'default' } : null
		),

	input: (status: InputStatus) => baseInput(status),
	multiselectInput: (status: InputStatus) =>
		css(
			baseInput(status),
			/**
			 * Because multiselect layout accommodates in-container item display,
			 * input has different styles
			 */
			{
				display: 'flex',
				flex: '1 1 0%',
				paddingLeft: '0.5rem',
				paddingTop: '0px',
				paddingBottom: '0px',
				margin: '0px',
				// Prevents input from getting too squished
				minWidth: '4rem',
			},
			// Because you don't copy from input, change cursor
			status.readOnly && { cursor: 'default' }
		),

	comboboxButton: css(
		// Position this button to the right side of and on top of
		// the input such that it appears to be inside the input.
		// tw`absolute top-0 right-0`,

		// Give the user a big area to tap/click even if the button's icon is small.
		{
			minWidth: 40,
			height: 38,

			// Center the icon vertically and horizontally.
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-around',
		}
	),

	adornmentIcon: css({
		color: theme.colors.input.adornment,
		width: '1.25rem',
		height: '1.25rem',
		cursor: 'pointer',
	}),
	popover: (maxHeight?: string | number, maxWidth?: string | number) =>
		css(
			{
				backgroundColor: theme.colors.white,
			},
			su.roundedSm,
			su.shadowMd,
			{
				borderColor: theme.colors.input.focusedBorder,
				paddingTop: '0px',
				paddingBottom: '0px',
				overflow: 'auto',
				borderWidth: '1px',
				borderStyle: 'solid',
				outline: '2px solid transparent',
				outlineOffset: '2px',

				maxHeight,
				maxWidth,
				// Remove reach's :focus styles
				'&:focus-within': css({
					outline: '2px solid transparent',
					outlineOffset: '2px',
				}),
			}
		),

	list: css({
		paddingTop: '0.25rem',
		paddingBottom: '0.25rem',
		// Put space between children.
		'& > * + *': css({ marginTop: '0.25rem' }),
		// Remove reach's :focus styles
		'&:focus': css({ outline: '2px solid transparent', outlineOffset: '2px' }),
	}),
	listItem: css(typography.regular, {
		display: 'flex',
		justifyContent: 'left',
		paddingLeft: '0.75rem',
		paddingRight: '0.75rem',
		paddingTop: '0.25rem',
		paddingBottom: '0.25rem',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.colors.background.dropdown.hoverItemBackground,
		},
		'&[data-highlighted], &.Mui-focusVisible': {
			backgroundColor: theme.colors.background.dropdown.selectItemBackground,
		},

		'&[aria-selected="true"]': css({
			color: theme.colors.palettes.primary.main,
		}),

		'&[aria-disabled="true"]': {
			color: theme.colors.text.hint,
			cursor: 'default',

			// disable hover styles
			'&:hover': { backgroundColor: 'inherit' },
		},
	}),
	// Centers the text in each selection button (ComboboxOption) so
	// that text is directly in the middle of the button.
	// Apply text-align to parent to align the text left or right.
	// Stops longer text from clipping into the selection button below
	text: css({
		transform: 'translateY(-50%)',
		position: 'relative',
		top: '50%',
	}),
	highlightMatch: {
		'& [data-user-value]': {
			backgroundColor: 'yellow',
		},
	},
	listTitle: css(typography.regular, {
		color: theme.colors.text.faint,
		paddingLeft: '0.75rem',
		paddingRight: '0.75rem',
		paddingTop: '0.25rem',
		paddingBottom: '0.25rem',
	}),
	emptyListItem: css(
		{ paddingLeft: '0.5rem', paddingRight: '0.5rem', paddingBottom: '0.5rem' },
		typography.emptySmall
	),
	tagValueContainer: css({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		paddingRight: '0.5rem',
	}),
	tagEditWrapper: css({ display: 'flex', alignItems: 'center' }),
	label: css(typography.label, {
		paddingLeft: '0.75rem',
		marginBottom: '0.25rem',
		minHeight: '0.9rem',
	}),
};
